import React, { useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthProvider";
import { useTheme } from "../contexts/ThemeContext";
import { Menu, Activity, Mail, Sun, Moon } from "lucide-react";
import { MobileMenu } from "./MobileMenu";
import { BottomNav } from "./BottomNav";
import MobileBottomNavigation from "./mobile/MobileBottomNavigation";
import { ApiTracker } from "./ApiTracker";
import { getCurrentApiCalls } from "../services/apiTrackerState";
import { useMobile } from "../hooks/useMobile";
import { useEdgeSwipeBack } from "../hooks/useEdgeSwipeBack";
import { NotificationTray } from "./NotificationTray";
import { UserDropdown } from "./UserDropdown";
import FloatingFeedbackButton from "./FloatingFeedbackButton";
import { PrefetchLink } from "./PrefetchLink";
import { meetupApi } from "../services/meetupApi";
import { isAdmin } from "../utils/adminUtils";
import ScrollToTop from "./ScrollToTop";
import { OfflineIndicator } from "./OfflineIndicator";
import { OfflineQueueManager } from "./mobile/OfflineQueueManager";
import SkipNavigation from "./common/SkipNavigation";

interface AppLayoutProps {
  children: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isApiTrackerVisible, setIsApiTrackerVisible] = React.useState(false);
  const [pendingInvitations, setPendingInvitations] = React.useState(0);
  const [apiCallCount, setApiCallCount] = React.useState(0);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);

  // Enable edge-swipe back navigation on mobile (swipe from left edge to go back)
  useEdgeSwipeBack({ enabled: isMobile });

  // Memoize the mobile menu close handler to prevent unnecessary re-renders
  const handleCloseMobileMenu = React.useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  // Handler to scroll to top when navigation links are clicked
  const handleNavLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      console.log("[AppLayout] Starting logout process...");
      await signOut();
      console.log("[AppLayout] Logout completed");
      // Navigation is handled by signOut() function
    } catch (error) {
      console.error("[AppLayout] Logout error:", error);
      // If signOut fails, manually navigate to login
      navigate("/login", { replace: true });
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Fetch pending invitations count
  useEffect(() => {
    const fetchInvitations = async () => {
      if (user) {
        try {
          const invitations = await meetupApi.getMyInvitations();
          const pendingCount = invitations.filter(
            (inv: any) => inv.status === "pending",
          ).length;
          setPendingInvitations(pendingCount);
        } catch (error) {
          console.error("Failed to fetch invitations:", error);
        }
      }
    };

    fetchInvitations();
    // Poll for new invitations every 30 seconds
    const interval = setInterval(fetchInvitations, 30000);

    // Listen for invitation response events to refresh count immediately
    const handleInvitationResponse = () => {
      fetchInvitations();
    };

    window.addEventListener("invitation-responded", handleInvitationResponse);

    return () => {
      clearInterval(interval);
      window.removeEventListener(
        "invitation-responded",
        handleInvitationResponse,
      );
    };
  }, [user]);

  // Update API call count periodically
  useEffect(() => {
    const updateApiCount = () => {
      const apiCalls = getCurrentApiCalls();
      const totalApiCalls = Object.values(apiCalls).reduce(
        (sum, call: any) => sum + call.count,
        0,
      );
      setApiCallCount(totalApiCalls);
    };

    // Initial update
    updateApiCount();

    // Update every second when API tracker is visible or admin
    if (isAdmin(user)) {
      const interval = setInterval(updateApiCount, 1000);
      return () => clearInterval(interval);
    }
  }, [isApiTrackerVisible, user]);

  return (
      <div className="flex flex-col min-h-screen bg-background">
        <SkipNavigation />
        <ScrollToTop />
        <OfflineIndicator />
        <OfflineQueueManager />
        {/* Header */}
        <header
          className="fixed top-0 left-0 right-0 z-50 bg-gray-800 dark:bg-gray-900 shadow-lg"
          role="banner"
        >
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              {/* Logo */}
              <Link
                to="/"
                className="flex items-center group"
                aria-label="Vitly Home"
                data-testid="nav-logo"
                onClick={handleNavLinkClick}
              >
                <img
                  src="/logo.svg"
                  alt="Vitly"
                  className="h-10 w-auto transition-all duration-300 group-hover:scale-105"
                />
              </Link>

              {/* Desktop Navigation */}
              <nav
                className="hidden md:flex items-center space-x-6"
                role="navigation"
                aria-label="Main navigation"
              >
                <PrefetchLink
                  to="/dashboard"
                  data-onboarding="dashboard"
                  data-testid="nav-dashboard"
                  className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
                  onClick={handleNavLinkClick}
                >
                  Dashboard
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </PrefetchLink>
                <PrefetchLink
                  to="/meetups"
                  data-onboarding="meetups"
                  data-testid="nav-meetups"
                  className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
                  onClick={handleNavLinkClick}
                >
                  Meetups
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </PrefetchLink>

                <PrefetchLink
                  to="/friends"
                  data-onboarding="friends"
                  data-testid="nav-friends"
                  className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
                  onClick={handleNavLinkClick}
                >
                  Friends
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </PrefetchLink>
                <PrefetchLink
                  to="/groups"
                  data-onboarding="groups"
                  data-testid="nav-groups"
                  className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
                  onClick={handleNavLinkClick}
                >
                  Groups
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </PrefetchLink>
                <PrefetchLink
                  to="/help"
                  data-onboarding="help"
                  data-testid="nav-help"
                  className="relative text-white/90 hover:text-white font-medium transition-all duration-300 group"
                  onClick={handleNavLinkClick}
                >
                  Help
                  <span className="absolute inset-x-0 bottom-0 h-0.5 bg-white transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></span>
                </PrefetchLink>
              </nav>

              {/* User Menu */}
              <div className="flex items-center space-x-3">
                  {/* Theme Toggle Button - Hidden on mobile */}
                <button
                  onClick={toggleTheme}
                  className="hidden md:flex p-2 rounded-lg hover:bg-white/10 text-white/90 hover:text-white transition-all duration-200"
                  aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
                  title={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
                >
                  {resolvedTheme === "dark" ? (
                    <Sun className="w-5 h-5" aria-hidden="true" />
                  ) : (
                    <Moon className="w-5 h-5" aria-hidden="true" />
                  )}
                </button>

                {/* API Monitor Toggle - Admin Only - Hidden on mobile */}
                {isAdmin(user) && (
                  <button
                    onClick={() => setIsApiTrackerVisible(!isApiTrackerVisible)}
                    className={`hidden md:flex relative p-2 rounded-lg transition-all duration-200 ${
                      isApiTrackerVisible
                        ? "bg-white/20 text-white"
                        : "hover:bg-white/10 text-white/90 hover:text-white"
                    }`}
                    aria-label="Toggle API Monitor"
                    aria-expanded={isApiTrackerVisible}
                    title={`API Monitor (Admin) - ${apiCallCount} API calls`}
                  >
                    <Activity className="w-5 h-5" aria-hidden="true" />
                    {apiCallCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                        {apiCallCount > 99 ? "99+" : apiCallCount}
                      </span>
                    )}
                  </button>
                )}

                {/* Invitations - Hidden on mobile */}
                {user && (
                  <Link
                    to="/invitations"
                    data-onboarding="invitations"
                    className="hidden md:flex relative p-2 rounded-lg hover:bg-white/10 text-white/90 hover:text-white transition-all duration-200"
                    title="Meetup Invitations"
                  >
                    <Mail className="w-5 h-5" />
                    {pendingInvitations > 0 && (
                      <span className="absolute -top-1 -right-1 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                        {pendingInvitations}
                      </span>
                    )}
                  </Link>
                )}

                {/* Notifications */}
                {user && (
                  <div data-onboarding="notifications">
                    <NotificationTray className="flex items-center" />
                  </div>
                )}

                {/* User Dropdown */}
                {user && (
                  <UserDropdown
                    user={user}
                    onLogout={handleLogout}
                    isLoggingOut={isLoggingOut}
                  />
                )}

                {/* Mobile menu button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="md:hidden p-2 rounded-lg hover:bg-white/10 text-white/90 hover:text-white transition-all duration-200 active:scale-95"
                  aria-label="Toggle mobile menu"
                  aria-expanded={isMobileMenuOpen}
                  aria-controls="mobile-navigation"
                >
                  <Menu
                    className={`w-5 h-5 transition-transform duration-200 ${isMobileMenuOpen ? "rotate-90" : ""}`}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Menu */}
        <MobileMenu
          isOpen={isMobileMenuOpen}
          onClose={handleCloseMobileMenu}
          theme={resolvedTheme}
          onToggleTheme={toggleTheme}
          apiCallCount={apiCallCount}
          onToggleApiTracker={() =>
            setIsApiTrackerVisible(!isApiTrackerVisible)
          }
          pendingInvitations={pendingInvitations}
        />

        {/* Main Content */}
        <main
          id="main-content"
          role="main"
          aria-label="Main content"
          tabIndex={-1}
          className={`flex-1 outline-none${location.pathname.match(/^\/meetups\/[^/]+$/) ? "" : " pt-16"}`}
        >
          {children}
        </main>

        {/* Bottom Navigation - Use MUI version on mobile, fallback to original otherwise */}
        {isMobile ? <MobileBottomNavigation /> : <BottomNav />}

        {/* Feedback Widget - Available for all logged in users */}
        {user && (
          <div data-onboarding="feedback">
            <FloatingFeedbackButton />
          </div>
        )}


        {/* PWA Install Prompt - temporarily disabled */}
        {/* <PWAInstallPrompt /> */}

        {/* API Tracker Tray - Admin Only */}
        {isAdmin(user) && (
          <ApiTracker
            isVisible={isApiTrackerVisible}
            onToggleVisibility={() => setIsApiTrackerVisible(false)}
          />
        )}
      </div>
  );
};
